<template>
  <div class="swap-interface">
    <!-- Seleção de Pares de Trading -->
    <v-card class="mb-6 swap-card">
      <v-card-title class="swap-title">
        <v-icon color="primary" class="mr-2">mdi-swap-horizontal</v-icon>
        Swap Cross-Chain
      </v-card-title>
      
      <v-card-text>
        <!-- Token de origem (Envia) -->
        <div class="token-panel mb-2">
          <div class="token-panel-header mb-2">
            <div class="token-panel-label">Você Envia</div>
            <div class="token-panel-balance" v-if="sourceToken">
              Saldo: {{ formatTokenAmount(sourceBalance, sourceToken.decimals) }} {{ sourceToken.symbol }}
            </div>
          </div>
          
          <div class="token-panel-content">
            <!-- Seletor de rede e token -->
            <div class="token-selector">
              <v-menu :close-on-content-click="false" location="bottom">
                <template v-slot:activator="{ props }">
                  <v-btn
                    variant="outlined"
                    v-bind="props"
                    class="token-select-btn"
                  >
                    <div class="d-flex align-center" v-if="sourceToken">
                      <v-avatar size="24" :color="getNetworkColor(sourceNetwork)" class="mr-2">
                        <span class="text-caption">{{ sourceNetwork?.icon }}</span>
                      </v-avatar>
                      <span class="mr-2">{{ sourceToken.symbol }}</span>
                      <v-icon size="small">mdi-chevron-down</v-icon>
                    </div>
                    <div v-else>
                      Selecione Token
                      <v-icon size="small" class="ml-2">mdi-chevron-down</v-icon>
                    </div>
                  </v-btn>
                </template>
                
                <v-card min-width="300" class="token-selector-menu">
                  <v-card-title>Selecione Token</v-card-title>
                  <v-card-text>
                    <!-- Seletor de rede -->
                    <v-select
                      v-model="sourceNetwork"
                      :items="networks"
                      item-title="name"
                      item-value="id"
                      label="Rede"
                      return-object
                      variant="outlined"
                      density="compact"
                      class="mb-4"
                    >
                      <template v-slot:selection="{ item }">
                        <div class="d-flex align-center">
                          <v-avatar size="24" :color="item.raw.color" class="mr-2">
                            <span class="text-caption">{{ item.raw.icon }}</span>
                          </v-avatar>
                          {{ item.raw.name }}
                        </div>
                      </template>
                      <template v-slot:item="{ item, props }">
                        <v-list-item v-bind="props">
                          <template v-slot:prepend>
                            <v-avatar size="24" :color="item.raw.color">
                              <span class="text-caption">{{ item.raw.icon }}</span>
                            </v-avatar>
                          </template>
                          <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                        </v-list-item>
                      </template>
                    </v-select>
                    
                    <!-- Lista de tokens disponíveis -->
                    <v-list class="token-list">
                      <v-list-item
                        v-for="token in getAvailableTokens(sourceNetwork?.id)"
                        :key="token.address"
                        @click="selectSourceToken(token)"
                        :active="sourceToken?.address === token.address"
                      >
                        <template v-slot:prepend>
                          <v-avatar size="32" class="mr-2">
                            <v-img :src="token.logo" alt="Token Logo"></v-img>
                          </v-avatar>
                        </template>
                        <v-list-item-title>{{ token.symbol }}</v-list-item-title>
                        <v-list-item-subtitle>{{ token.name }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
            
            <!-- Campo de quantidade -->
            <div class="token-amount">
              <v-text-field
                v-model="sourceAmount"
                variant="outlined"
                density="compact"
                type="number"
                min="0"
                step="0.000001"
                class="token-amount-input"
                hide-details
                @input="updateTargetAmount"
              ></v-text-field>
              
              <v-btn
                size="small"
                variant="text"
                color="primary"
                class="max-btn"
                @click="setMaxAmount"
              >MAX</v-btn>
            </div>
          </div>
        </div>
        
        <!-- Botão de inversão -->
        <div class="swap-direction-btn my-2 d-flex justify-center">
          <v-btn
            icon
            variant="outlined"
            color="primary"
            @click="swapDirection"
            class="flip-btn"
          >
            <v-icon>mdi-swap-vertical</v-icon>
          </v-btn>
        </div>
        
        <!-- Token de destino (Recebe) -->
        <div class="token-panel">
          <div class="token-panel-header mb-2">
            <div class="token-panel-label">Você Recebe</div>
            <div class="token-panel-balance" v-if="targetToken">
              Saldo: {{ formatTokenAmount(targetBalance, targetToken.decimals) }} {{ targetToken.symbol }}
            </div>
          </div>
          
          <div class="token-panel-content">
            <!-- Seletor de rede e token -->
            <div class="token-selector">
              <v-menu :close-on-content-click="false" location="bottom">
                <template v-slot:activator="{ props }">
                  <v-btn
                    variant="outlined"
                    v-bind="props"
                    class="token-select-btn"
                  >
                    <div class="d-flex align-center" v-if="targetToken">
                      <v-avatar size="24" :color="getNetworkColor(targetNetwork)" class="mr-2">
                        <span class="text-caption">{{ targetNetwork?.icon }}</span>
                      </v-avatar>
                      <span class="mr-2">{{ targetToken.symbol }}</span>
                      <v-icon size="small">mdi-chevron-down</v-icon>
                    </div>
                    <div v-else>
                      Selecione Token
                      <v-icon size="small" class="ml-2">mdi-chevron-down</v-icon>
                    </div>
                  </v-btn>
                </template>
                
                <v-card min-width="300" class="token-selector-menu">
                  <v-card-title>Selecione Token</v-card-title>
                  <v-card-text>
                    <!-- Seletor de rede -->
                    <v-select
                      v-model="targetNetwork"
                      :items="networks"
                      item-title="name"
                      item-value="id"
                      label="Rede"
                      return-object
                      variant="outlined"
                      density="compact"
                      class="mb-4"
                    >
                      <template v-slot:selection="{ item }">
                        <div class="d-flex align-center">
                          <v-avatar size="24" :color="item.raw.color" class="mr-2">
                            <span class="text-caption">{{ item.raw.icon }}</span>
                          </v-avatar>
                          {{ item.raw.name }}
                        </div>
                      </template>
                      <template v-slot:item="{ item, props }">
                        <v-list-item v-bind="props">
                          <template v-slot:prepend>
                            <v-avatar size="24" :color="item.raw.color">
                              <span class="text-caption">{{ item.raw.icon }}</span>
                            </v-avatar>
                          </template>
                          <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                        </v-list-item>
                      </template>
                    </v-select>
                    
                    <!-- Lista de tokens disponíveis -->
                    <v-list class="token-list">
                      <v-list-item
                        v-for="token in getAvailableTokens(targetNetwork?.id)"
                        :key="token.address"
                        @click="selectTargetToken(token)"
                        :active="targetToken?.address === token.address"
                      >
                        <template v-slot:prepend>
                          <v-avatar size="32" class="mr-2">
                            <v-img :src="token.logo" alt="Token Logo"></v-img>
                          </v-avatar>
                        </template>
                        <v-list-item-title>{{ token.symbol }}</v-list-item-title>
                        <v-list-item-subtitle>{{ token.name }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
            
            <!-- Campo de quantidade -->
            <div class="token-amount">
              <v-text-field
                v-model="targetAmount"
                variant="outlined"
                density="compact"
                type="number"
                readonly
                class="token-amount-input"
                hide-details
              ></v-text-field>
            </div>
          </div>
        </div>
        
        <!-- Informações de swap -->
        <div class="swap-info mt-6" v-if="sourceToken && targetToken">
          <div class="d-flex justify-space-between mb-2">
            <div class="text-caption">{{ t('sale.exchangeRate') }}</div>
            <div>
              1 {{ sourceToken.symbol }} ≈ {{ formatNumber(exchangeRate) }} {{ targetToken.symbol }}
            </div>
          </div>
          
          <div class="d-flex justify-space-between mb-2">
            <div class="text-caption">Taxa ({{ swapFeePercentage }}%)</div>
            <div>
              {{ formatTokenAmount(swapFee, sourceToken.decimals) }} {{ sourceToken.symbol }}
            </div>
          </div>
          
          <div class="d-flex justify-space-between mb-2">
            <div class="text-caption">Tempo estimado</div>
            <div>{{ estimatedTime }}</div>
          </div>
          
          <div class="d-flex justify-space-between">
            <div class="text-caption">Rota</div>
            <div class="d-flex align-center">
              {{ sourceToken.symbol }}
              <v-icon size="small" class="mx-1">mdi-arrow-right</v-icon>
              {{ targetToken.symbol }}
            </div>
          </div>
        </div>
        
        <!-- Botão de swap -->
        <v-btn
          block
          color="primary"
          size="large"
          class="mt-6"
          :disabled="!canSwap"
          @click="initiateSwap"
        >
          {{ swapButtonText }}
        </v-btn>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { useDexStore } from '@/stores/dex'

export default {
  name: 'SwapInterface',
  
  data() {
    return {
      sourceNetwork: null,
      targetNetwork: null,
      sourceToken: null,
      targetToken: null,
      sourceAmount: '',
      targetAmount: '',
      swapFeePercentage: 0.5,
      networks: [
        {
          id: 'quarnx',
          name: 'QuarnX',
          icon: 'Q',
          color: 'primary',
          chainId: '1'
        },
        {
          id: 'ethereum',
          name: 'Ethereum',
          icon: 'E',
          color: '#627EEA',
          chainId: '1'
        },
        {
          id: 'bsc',
          name: 'BSC',
          icon: 'B',
          color: '#F3BA2F',
          chainId: '56'
        },
        {
          id: 'polkadot',
          name: 'Polkadot',
          icon: 'P',
          color: '#E6007A',
          chainId: '0'
        }
      ],
      // Swap em desenvolvimento - dados carregados da API quando disponível
      tokensData: {},
      // Taxas de câmbio (carregadas da API quando DEX estiver disponível)
      exchangeRates: {}
    }
  },
  
  computed: {
    dexStore() {
      return useDexStore()
    },
    
    sourceBalance() {
      return this.sourceToken ? this.sourceToken.balance : '0'
    },
    
    targetBalance() {
      return this.targetToken ? this.targetToken.balance : '0'
    },
    
    exchangeRate() {
      if (!this.sourceToken || !this.targetToken) return 0
      
      const key = `${this.sourceToken.symbol}_${this.targetToken.symbol}`
      const reverseKey = `${this.targetToken.symbol}_${this.sourceToken.symbol}`
      
      if (this.exchangeRates[key]) {
        return this.exchangeRates[key]
      } else if (this.exchangeRates[reverseKey]) {
        return 1 / this.exchangeRates[reverseKey]
      } else {
        return 0
      }
    },
    
    swapFee() {
      if (!this.sourceAmount || !this.sourceToken) return '0'
      return (parseFloat(this.sourceAmount) * (this.swapFeePercentage / 100) * Math.pow(10, this.sourceToken.decimals)).toString()
    },
    
    estimatedTime() {
      // Tempo estimado baseado se é cross-chain ou não
      if (this.sourceNetwork?.id === this.targetNetwork?.id) {
        return '< 1 minuto'
      } else {
        return '3-10 minutos'
      }
    },
    
    canSwap() {
      return (
        this.sourceToken && 
        this.targetToken && 
        this.sourceAmount && 
        parseFloat(this.sourceAmount) > 0 &&
        this.validateBalance()
      )
    },
    
    swapButtonText() {
      if (!this.sourceToken || !this.targetToken) {
        return 'Swap em desenvolvimento'
      } else if (!this.sourceAmount || parseFloat(this.sourceAmount) <= 0) {
        return 'Digite um valor'
      } else if (!this.validateBalance()) {
        return 'Saldo insuficiente'
      } else {
        return 'Trocar'
      }
    }
  },
  
  methods: {
    getNetworkColor(network) {
      return network?.color || 'grey'
    },
    
    getAvailableTokens(networkId) {
      if (!networkId || !this.tokensData[networkId]) return []
      return this.tokensData[networkId]
    },
    
    selectSourceToken(token) {
      this.sourceToken = token
      this.updateTargetAmount()
    },
    
    selectTargetToken(token) {
      this.targetToken = token
      this.updateTargetAmount()
    },
    
    updateTargetAmount() {
      if (!this.sourceAmount || !this.sourceToken || !this.targetToken || !this.exchangeRate) {
        this.targetAmount = ''
        return
      }
      
      const amount = parseFloat(this.sourceAmount) * this.exchangeRate
      this.targetAmount = amount.toFixed(6)
    },
    
    swapDirection() {
      // Trocar tokens de origem e destino
      const tempNetwork = this.sourceNetwork
      const tempToken = this.sourceToken
      const tempAmount = this.sourceAmount
      
      this.sourceNetwork = this.targetNetwork
      this.sourceToken = this.targetToken
      this.sourceAmount = this.targetAmount
      
      this.targetNetwork = tempNetwork
      this.targetToken = tempToken
      this.targetAmount = tempAmount
    },
    
    setMaxAmount() {
      if (!this.sourceToken) return
      
      // Converter o saldo para o formato decimal
      const balance = this.formatTokenAmount(this.sourceBalance, this.sourceToken.decimals)
      this.sourceAmount = balance
      this.updateTargetAmount()
    },
    
    validateBalance() {
      if (!this.sourceToken || !this.sourceAmount) return false
      
      const balanceDecimal = parseFloat(this.formatTokenAmount(this.sourceBalance, this.sourceToken.decimals))
      return parseFloat(this.sourceAmount) <= balanceDecimal
    },
    
    initiateSwap() {
      // Swap em desenvolvimento - funcionalidade DEX ainda não disponível no kernel
      alert('Swap em desenvolvimento. A funcionalidade DEX será implementada em breve.')
    },
    
    formatTokenAmount(amount, decimals) {
      if (!amount) return '0'
      return (parseFloat(amount) / Math.pow(10, decimals)).toString()
    },
    
    formatNumber(num) {
      if (typeof num !== 'number') return '0'
      return num.toFixed(6)
    }
  },
  
  mounted() {
    // Swap em desenvolvimento - tokens serão carregados quando DEX estiver disponível
    this.sourceNetwork = this.networks.find(n => n.id === 'quarnx')
    this.targetNetwork = this.networks.find(n => n.id === 'ethereum')
  }
}
</script>

<style scoped>
.swap-card {
  max-width: 480px;
  margin: 0 auto;
  border-radius: 16px;
}

.swap-title {
  font-size: 1.25rem;
  font-weight: 600;
}

.token-panel {
  background-color: rgba(var(--v-theme-surface-variant), 0.1);
  border-radius: 12px;
  padding: 16px;
}

.token-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.token-panel-label {
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.8);
}

.token-panel-balance {
  font-size: 0.85rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.token-panel-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.token-selector {
  flex: 0 0 auto;
}

.token-select-btn {
  height: 48px;
  min-width: 120px;
}

.token-amount {
  flex: 1;
  position: relative;
}

.token-amount-input {
  width: 100%;
}

.max-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  font-weight: 600;
}

.swap-direction-btn {
  margin: -14px 0;
  position: relative;
  z-index: 1;
}

.flip-btn {
  background-color: var(--v-theme-surface);
  border: 2px solid var(--v-theme-surface-variant);
}

.swap-info {
  background-color: rgba(var(--v-theme-surface-variant), 0.1);
  border-radius: 12px;
  padding: 16px;
  font-size: 0.875rem;
}
</style>
